import React, { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import eventBus from "../utils/EventBus";
import { useNavigation } from '../contexts/NavigationContext';
import logger from '../services/LoggingService';
import { getAuthToken, handleUnauthenticated, isAuthenticated } from '../utils/AuthUtils';
import ColorPicker from './ColorPicker';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import SongInfoBar from './SongInfoBar';
import SheetHeader from './SheetHeader';
import Section from './Section';
import PartsModule from './PartsModule';
import ContextMenu from './ContextMenu';
import EnergyDialog from './EnergyDialog';
import ConfirmModal from './ConfirmModal';
import { useEditing } from '../contexts/EditingContext';
import { useSheetData } from '../contexts/SheetDataContext';
import { useUIState } from '../contexts/UIStateContext';
import { useNotifications } from '../contexts/NotificationContext';
import { getTransposedChords } from '../services/ChordService';
import { useAuth } from '../contexts/AuthContext';
import { getAllSheets, saveTemporaryDraft, loadTemporaryDraft, clearTemporaryDraft, hasTemporaryDraft, deleteSheet } from '../services/SheetStorageService';
import { generatePrintContent } from '../services/ExportService';
import { generatePrintUrl } from '../utils/PrintSettingsUtils';
import SheetLineupManager from './SheetLineupManager';
import CommentsSection from './Comments/CommentsSection';
import MediaUrlModal from './MediaUrlModal';
import NotationSection from './NotationSection';
import ShareModal from './ShareModal';

export default function BandSheetEditor({ 
  initialSheetId,
  // External toolbar integration props
  useExternalToolbar = false,
  externalSidebarOpen,
  externalSetSidebarOpen,
  externalSetlistsPanelOpen,
  externalSetSetlistsPanelOpen
  // Import/Export functionality moved to SheetsPage
}) {
  const location = useLocation();
  
  // For tracking previously loaded sheet to prevent duplicate loads
  const loadedSheetIdRef = useRef(null);
  const notificationShownRef = useRef(null);
  // State to track if we're on a mobile device
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Add resize listener to update isMobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Event listeners will be added after function declarations
  
  // State for setlists panel - use external state if provided
  const [internalSetlistsPanelOpen, setInternalSetlistsPanelOpen] = useState(false);
  
  // State for media URL modal
  const [mediaUrlModalOpen, setMediaUrlModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const setlistsPanelOpen = useExternalToolbar ? externalSetlistsPanelOpen : internalSetlistsPanelOpen;
  const setSetlistsPanelOpen = useExternalToolbar ? externalSetSetlistsPanelOpen : setInternalSetlistsPanelOpen;
  // Use centralized notification system instead of local state
  const { showNotification } = useNotifications();
  
  // State for new sheet confirmation dialog
  const [newSheetConfirm, setNewSheetConfirm] = useState({
    isOpen: false,
    onConfirm: () => {},
    onCancel: () => {}
  });
  
  // State for delete sheet confirmation dialog
  const [deleteSheetConfirm, setDeleteSheetConfirm] = useState({
    isOpen: false,
    onConfirm: () => {},
    onCancel: () => {}
  });
  
  // State for color picker
  const [colorPicker, setColorPicker] = useState({
    isOpen: false,
    x: 0,
    y: 0,
    sectionIndex: null,
    initialColor: '#ffffff'
  });
  
  // Access UI state from context
  const { showAuthModal } = useAuth();
  // Use our centralized authentication utilities instead of context-provided variables
  
  // State to track if we've loaded a draft
  const [draftLoaded, setDraftLoaded] = useState(false);
  
  // We're now using the centralized notification system via useNotifications hook
  
  // Navigation hooks for URL management
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if we're in perform mode based on URL parameters
  const isPrintMode = searchParams.get('perform') === 'true';
  const includeColors = searchParams.get('color') === 'true';
  const includeChords = searchParams.get('chords') === 'true';
  
  // Column visibility settings for perform mode (default to hidden on mobile)
  const getColumnVisibility = (columnName) => {
    const paramValue = searchParams.get(columnName);
    if (paramValue !== null) {
      // If explicitly set in URL, use that value
      return paramValue !== 'false';
    }
    // Default: hide on mobile, show on desktop
    return !isMobile;
  };
  
  const showSection = isPrintMode ? getColumnVisibility('section') : true;
  const showPart = isPrintMode ? getColumnVisibility('part') : true;
  const showBars = isPrintMode ? getColumnVisibility('bars') : true;
  const showNotes = isPrintMode ? getColumnVisibility('notes') : true;
  
  // Use SheetDataContext for all sheet data and operations
  const { 
    sections,
    songData, setSongData,
    createNewSheetData,
    currentSheetId,
    partsModule,
    transposeValue,
    // Operations
    addSection,
    deleteSection,
    moveSection,
    duplicateSection,
    addPart,
    deletePart,
    movePart,
    duplicatePart,
    // Sheet operations
    loadSheet,
    saveCurrentSheet,
    exportSheet,
    updateSectionEnergy,
    updateSectionBackgroundColor,
    getTransposedChordsForPart
  } = useSheetData();
  
  // Use EditingContext for editing state
  useEditing(); // Keep the context connection without destructuring
  
  // Use UIStateContext for UI-related state
  const {
    // Context menu state
    contextMenu, handleContextMenu, hideContextMenu,
    // Hover state
    hoverState, setHoverState,
    // Energy dialog state
    openEnergyDialog
  } = useUIState();
  
  // Get navigation state from NavigationContext
  const { loadedSheetId, navSource, setLoadedSheet } = useNavigation();
  
  
  // Removed unused location tracking
  
  
  // Load draft if no sheet ID is provided
  useEffect(() => {
    const loadDraftIfNeeded = async () => {
      if (!initialSheetId && !currentSheetId && !draftLoaded) {
        // Check for temporary draft if no sheet ID was provided
        const draft = loadTemporaryDraft();
        if (draft) {
          try {
            // Load the draft data into the editor
            createNewSheetData();
            setSongData(draft.songData || { title: '', artist: '', bpm: 120 });
            // Only set sections if they exist in the draft
            if (draft.sections && draft.sections.length > 0) {
              // We're using the context's internal state update mechanism
              // by calling createNewSheetData first and then manually updating
              for (let i = 0; i < draft.sections.length; i++) {
                if (i === 0) {
                  // Update the first section that was created by createNewSheetData
                  // This avoids having to manipulate the sections array directly
                  updateSectionBackgroundColor(0, draft.sections[i].backgroundColor || null);
                  // Add parts to match the draft
                  for (let j = 1; j < draft.sections[i].parts.length; j++) {
                    addPart(0);
                  }
                } else {
                  // Add additional sections
                  addSection();
                  updateSectionBackgroundColor(i, draft.sections[i].backgroundColor || null);
                  // Add parts to match the draft
                  for (let j = 1; j < draft.sections[i].parts.length; j++) {
                    addPart(i);
                  }
                }
              }
            }
            showNotification('Unsaved draft loaded', 'info');
            setDraftLoaded(true);
          } catch (error) {
            logger.error('BandSheetEditor', 'Error loading draft:', error);
            showNotification('Error loading draft', 'error');
          }
        }
      }
    };
    
    loadDraftIfNeeded();
  }, [initialSheetId, currentSheetId, draftLoaded, showNotification, createNewSheetData, setSongData, addSection, addPart, updateSectionBackgroundColor, loadTemporaryDraft, clearTemporaryDraft]);
  
  // Navigation is now handled through NavigationContext and SheetDataContext
  // Single sheet loading useEffect - this replaces all the problematic Redux middleware
  useEffect(() => {
    const loadSheetIfNeeded = async () => {
      // Determine which sheet ID to load - prioritize initialSheetId (from URL) over NavigationContext
      const sheetIdToLoad = initialSheetId || loadedSheetId;
      
      console.log('%c[NAVIGATION DEBUG]', 'color: green; font-weight: bold', {
        initialSheetId,
        loadedSheetId,
        sheetIdToLoad,
        currentSheetId,
        'loadedSheetIdRef.current': loadedSheetIdRef.current
      });
      
      if (!sheetIdToLoad || sheetIdToLoad === 'new') {
        // No sheet to load or creating new sheet
        if (sheetIdToLoad === 'new') {
          console.log('%c[NEW SHEET]', 'color: purple; font-weight: bold', 'Creating new sheet');
          logger.debug('BandSheetEditor', 'Creating new sheet');
          // Clear any previous loaded sheet reference
          loadedSheetIdRef.current = null;
          notificationShownRef.current = null;
          createNewSheetData();
        }
        return;
      }
      
      // CRITICAL: Enhanced duplicate prevention to avoid cascading loads
      if (loadedSheetIdRef.current === sheetIdToLoad) {
        logger.debug('BandSheetEditor', `Sheet ${sheetIdToLoad} already loaded via ref, skipping`);
        return;
      }
      
      // Additional check: if currentSheetId matches, we're already loaded
      if (currentSheetId === sheetIdToLoad) {
        logger.debug('BandSheetEditor', `Sheet ${sheetIdToLoad} already loaded via context, updating ref`);
        loadedSheetIdRef.current = sheetIdToLoad;
        return;
      }
      
      try {
        logger.debug('BandSheetEditor', `Loading sheet: ${sheetIdToLoad} (source: ${navSource || 'unknown'})`);
        loadedSheetIdRef.current = sheetIdToLoad;
        
        const success = await loadSheet(sheetIdToLoad);
        if (success) {
          // DON'T update NavigationContext here to avoid circular updates
          // setLoadedSheet(sheetIdToLoad, navSource);
          
          // Only show notification once per sheet to prevent duplicates
          if (notificationShownRef.current !== sheetIdToLoad) {
            notificationShownRef.current = sheetIdToLoad;
            showNotification('Sheet loaded successfully');
          }
        } else {
          logger.error('BandSheetEditor', `Failed to load sheet: ${sheetIdToLoad}`);
          showNotification('Failed to load sheet', 'error');
          loadedSheetIdRef.current = null;
        }
      } catch (error) {
        logger.error('BandSheetEditor', 'Error loading sheet:', error);
        
        // Special handling for "Sheet not found" errors that might be temporary
        if (error.message && error.message.includes('Sheet not found')) {
          logger.warn('BandSheetEditor', `Sheet ${sheetIdToLoad} not found - this might be a race condition after save`);
          showNotification('Sheet temporarily unavailable, please try again', 'warning');
        } else {
          showNotification('Error loading sheet: ' + error.message, 'error');
        }
        loadedSheetIdRef.current = null;
      }
    };
    
    loadSheetIfNeeded();
  }, [initialSheetId, loadedSheetId]);
  
  // Removed unused location tracking and saved sheets functionality
  
  // Combined saving functionality: keyboard shortcuts and auto-save drafts
  useEffect(() => {
    // Keyboard shortcut handler for saving (Cmd+S / Ctrl+S)
    const handleKeyDown = (e) => {
      // Check for Cmd+S (Mac) or Ctrl+S (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog

        if (isAuthenticated()) {
          saveCurrentSheet()
            .then((savedSheet) => {
              // CRITICAL FIX: Update URL if this was a new sheet (URL was /sheet/new OR currentSheetId is null)
              if ((initialSheetId === 'new' || !currentSheetId) && savedSheet && savedSheet._id) {
                logger.debug('BandSheetEditor', `Navigating from new sheet to /sheet/${savedSheet._id}`);
                // Update our loaded sheet reference to prevent immediate reload
                loadedSheetIdRef.current = savedSheet._id;
                notificationShownRef.current = savedSheet._id;
                navigate(`/sheet/${savedSheet._id}`, { replace: true });
              }
              
              showNotification('Sheet saved successfully');
              // Clear temporary draft after successful save
              clearTemporaryDraft();
            })
            .catch(error => {
              showNotification('Error saving sheet: ' + error.message, 'error');
            });
        } else {
          showNotification('Please log in to save your sheet', 'error');
        }
      }
    };
    
    // Auto-save draft for unauthenticated users
    let draftTimer;
    if (!isAuthenticated() && sections.length > 0) {
      draftTimer = setTimeout(() => {
        const currentSheet = {
          songData,
          sections
        };
        saveTemporaryDraft(currentSheet);
        logger.debug('BandSheetEditor', 'Temporary draft auto-saved');
      }, 30000); // Auto-save draft every 30 seconds
    }
    
    // Add keyboard event listener
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (draftTimer) {
        clearTimeout(draftTimer);
      }
    };
  }, [saveCurrentSheet, showNotification, sections, songData]);

  // Placeholder text for empty fields
  const placeholders = {
    lyrics: "Add lyrics here...",
    notes: "Add notes here..."
  };
  
  // Debugging if needed can be done through browser devtools

  // These operations are now handled by SheetDataContext


  // We're now using handleContextMenu directly from UIStateContext
  // No need for a local wrapper function
  
  // Get context menu items based on context type
  const getContextMenuItems = () => {
    const { type, si, pi } = contextMenu;
    const menuItems = [];
    
    if (type === 'section') {
      // Only show Move Up if not the first section
      if (si > 0) {
        menuItems.push({
          label: 'Move Up',
          action: () => handleMenuAction('moveUp')
        });
      }
      
      // Only show Move Down if not the last section
      if (si < sections.length - 1) {
        menuItems.push({
          label: 'Move Down',
          action: () => handleMenuAction('moveDown')
        });
      }
      
      menuItems.push({
        label: 'Set Energy Level',
        action: () => handleMenuAction('setEnergyLevel')
      });
      
      menuItems.push({
        label: 'Set Background Color',
        action: () => handleMenuAction('setBackgroundColor')
      });
      
      menuItems.push({
        label: 'Duplicate Section',
        action: () => handleMenuAction('duplicate')
      });
      
      menuItems.push({
        label: 'Delete Section',
        action: () => {
          // Call deleteSection directly on click
          try {
            // Get the section index from contextMenu directly at execution time
            const index = Number(contextMenu.si);
            logger.debug('BandSheetEditor', `Deleting section at index: ${index}`);
            
            // Hide the context menu
            hideContextMenu();
            
            // Use the more direct approach, bypass the timeout
            deleteSection(index);
            
            // Show notification
            showNotification('Section deleted successfully');
          } catch (error) {
            logger.error('BandSheetEditor', 'Delete section error:', error);
            showNotification(`Error deleting section: ${error.message}`, 'error');
          }
        },
        danger: true
      });
    } else if (type === 'part') {
      // Only show Move Up if not the first part
      if (pi > 0) {
        menuItems.push({
          label: 'Move Up',
          action: () => handleMenuAction('moveUp')
        });
      }
      
      // Only show Move Down if not the last part
      if (pi < sections[si]?.parts.length - 1) {
        menuItems.push({
          label: 'Move Down',
          action: () => handleMenuAction('moveDown')
        });
      }
      
      menuItems.push({
        label: 'Add Part',
        action: () => handleMenuAction('add')
      });
      
      menuItems.push({
        label: 'Duplicate Part',
        action: () => handleMenuAction('duplicate')
      });
      
      menuItems.push({
        label: 'Delete Part',
        action: () => {
          // Call deletePart directly on click
          try {
            // Get the indices from contextMenu directly at execution time
            const sectionIndex = Number(contextMenu.si);
            const partIndex = Number(contextMenu.pi);
            logger.debug('BandSheetEditor', `Deleting part at section: ${sectionIndex}, part: ${partIndex}`);
            
            // Hide the context menu
            hideContextMenu();
            
            // Use the more direct approach, bypass the timeout
            deletePart(sectionIndex, partIndex);
            
            // Show notification
            showNotification('Part deleted successfully');
          } catch (error) {
            logger.error('BandSheetEditor', 'Delete part error:', error);
            showNotification(`Error deleting part: ${error.message}`, 'error');
          }
        },
        danger: true
      });
    }
    
    return menuItems;
  };

  // Direct menu action handlers - these are defined outside the main handler to ensure they work correctly
  const handleDeleteSection = (sectionIndex) => {
    // Use the deleteSection function from SheetDataContext
    try {
      deleteSection(sectionIndex);
      showNotification('Section deleted successfully');
    } catch (error) {
      logger.error('BandSheetEditor', 'Error deleting section:', error);
      showNotification(`Failed to delete section: ${error.message}`, 'error');
    }
  };
  
  const handleDeletePart = (sectionIndex, partIndex) => {
    // Use the deletePart function from SheetDataContext
    try {
      deletePart(sectionIndex, partIndex);
      showNotification('Part deleted successfully');
    } catch (error) {
      logger.error('BandSheetEditor', 'Error deleting part:', error);
      showNotification(`Failed to delete part: ${error.message}`, 'error');
    }
  };
  
  const handleMediaUrlSave = (url) => {
    // Update the song data with the new media URL
    setSongData(prev => ({
      ...prev,
      mediaUrl: url
    }));
    
    if (url) {
      showNotification('Media URL added successfully');
    } else {
      showNotification('Media URL removed');
    }
  };
  
  // Menu actions - completely rewritten for reliability
  const handleMenuAction = (action) => {
    // Store the values locally to avoid using stale contextMenu state
    // Convert indices to numbers to ensure consistent handling
    const type = contextMenu.type;
    const sectionIndex = Number(contextMenu.si);
    const partIndex = contextMenu.pi !== null ? Number(contextMenu.pi) : null;
    
    // Close the context menu first to prevent stale state issues
    hideContextMenu();
    
    // Clear hover state to prevent menu icon from showing on wrong section
    setHoverState({ type: null, si: null, pi: null });
    
    // Handle each action type directly
    switch (action) {
      case 'setBackgroundColor':
        if (type === 'section') {
          const currentColor = sections[sectionIndex]?.backgroundColor || '#ffffff';
          setColorPicker({
            isOpen: true,
            x: contextMenu.x,
            y: contextMenu.y,
            sectionIndex,
            initialColor: currentColor
          });
        }
        break;
        
      case 'duplicate':
        if (type === 'section') {
          setTimeout(() => duplicateSection(sectionIndex), 50);
        } else if (type === 'part') {
          setTimeout(() => duplicatePart(sectionIndex, partIndex), 50);
        }
        break;
        
      case 'delete':
        if (type === 'section') {
          // Use the direct handler to ensure this works reliably
          setTimeout(() => handleDeleteSection(sectionIndex), 50);
        } else if (type === 'part') {
          setTimeout(() => handleDeletePart(sectionIndex, partIndex), 50);
        }
        break;
        
      case 'moveUp':
        if (type === 'section') {
          setTimeout(() => moveSection(sectionIndex, 'up'), 50);
        } else if (type === 'part') {
          setTimeout(() => movePart(sectionIndex, partIndex, 'up'), 50);
        }
        break;
        
      case 'moveDown':
        if (type === 'section') {
          setTimeout(() => moveSection(sectionIndex, 'down'), 50);
        } else if (type === 'part') {
          setTimeout(() => movePart(sectionIndex, partIndex, 'down'), 50);
        }
        break;
        
      case 'setEnergyLevel':
        if (type === 'section') {
          setTimeout(() => openEnergyDialog(sectionIndex), 50);
        }
        break;
        
      case 'add':
        if (type === 'part') {
          setTimeout(() => addPart(sectionIndex), 50);
        }
        break;
        
      default:
        // Unknown action
        break;
    }
  };

  // Note: Energy dialog functions have been moved to the EnergyDialog component

  // New Sheet handler - navigates to /sheet/new for consistent behavior
  const handleNewSheet = () => {
    // Check if there are unsaved changes
    if (sections.length > 0) {
      setNewSheetConfirm({
        isOpen: true,
        onConfirm: async () => {
          try {
            // Use centralized AuthUtils for authentication check
            if (isAuthenticated()) {
              // Save current sheet first if authenticated
              await saveCurrentSheet();
              showNotification('Sheet saved before creating new sheet');
            } else {
              // Save as draft if not authenticated
              const currentSheet = {
                songData,
                sections
              };
              saveTemporaryDraft(currentSheet);
              showNotification('Draft saved before creating new sheet', 'info');
            }
            
            // Navigate to new sheet URL for consistent behavior
            navigate('/sheet/new');
            showNotification('New sheet created');
          } catch (error) {
            logger.error('BandSheetEditor', 'Error saving before new sheet:', error);
            showNotification('Error saving sheet: ' + error.message, 'error');
          } finally {
            setNewSheetConfirm({ isOpen: false, onConfirm: () => {}, onCancel: () => {} });
          }
        },
        onCancel: () => {
          // Just close the dialog without saving or creating new sheet
          setNewSheetConfirm({ isOpen: false, onConfirm: () => {}, onCancel: () => {} });
        }
      });
    } else {
      // No existing content, just navigate to new sheet
      navigate('/sheet/new');
      showNotification('New sheet created');
    }
  };



  // Simple implementation of save that matches EXACTLY what the keyboard shortcut does
  const handleSave = async () => {
    console.log('%c[SAVE] Using exact keyboard shortcut implementation', 'color: blue; font-weight: bold');
    
    if (isAuthenticated()) {
      try {
        // Call saveCurrentSheet without any parameters - exactly like the keyboard shortcut
        const savedSheet = await saveCurrentSheet();
        
        // CRITICAL FIX: Update URL if this was a new sheet (URL was /sheet/new OR currentSheetId is null)
        if ((initialSheetId === 'new' || !currentSheetId) && savedSheet && savedSheet._id) {
          logger.debug('BandSheetEditor', `Navigating from new sheet to /sheet/${savedSheet._id}`);
          // Update our loaded sheet reference to prevent immediate reload
          loadedSheetIdRef.current = savedSheet._id;
          notificationShownRef.current = savedSheet._id;
          navigate(`/sheet/${savedSheet._id}`, { replace: true });
        }
        
        showNotification('Sheet saved successfully');
        clearTemporaryDraft();
        return savedSheet;
      } catch (error) {
        console.error('[SAVE] Save failed:', error);
        showNotification('Error saving sheet: ' + error.message, 'error');
        throw error;
      }
    } else {
      console.log('[SAVE] User is not authenticated');
      showNotification('Please log in to save your sheet', 'error');
      return null;
    }
  } // End of handleSave function
  
  // Delete Sheet handler - shows confirmation dialog before deleting
  const handleDeleteSheet = () => {
    // Don't allow deleting unsaved sheets
    if (!currentSheetId || currentSheetId === 'new') {
      showNotification('Cannot delete an unsaved sheet', 'error');
      return;
    }
    
    setDeleteSheetConfirm({
      isOpen: true,
      onConfirm: async () => {
        try {
          logger.debug('BandSheetEditor', 'Deleting sheet:', currentSheetId);
          const success = await deleteSheet(currentSheetId);
          
          if (success) {
            showNotification('Sheet deleted successfully');
            // Navigate to sheets page after successful deletion
            navigate('/sheets');
          } else {
            showNotification('Failed to delete sheet', 'error');
          }
        } catch (error) {
          logger.error('BandSheetEditor', 'Error deleting sheet:', error);
          showNotification('Error deleting sheet: ' + error.message, 'error');
        } finally {
          setDeleteSheetConfirm({ isOpen: false, onConfirm: () => {}, onCancel: () => {} });
        }
      },
      onCancel: () => {
        setDeleteSheetConfirm({ isOpen: false, onConfirm: () => {}, onCancel: () => {} });
      }
    });
  };
  
  // Add event listeners for editor actions now that functions are declared
  useEffect(() => {
    // Listen for editor events from the AppLayout
    const newSheetUnsub = eventBus.on('editor:new', () => {
      logger.debug('BandSheetEditor', 'New sheet event received');
      handleNewSheet();
    });
    
    const saveUnsub = eventBus.on('editor:save', () => {
      handleSave();
    });
    
    const pdfUnsub = eventBus.on('editor:pdf', () => {
      logger.debug('BandSheetEditor', 'Preview event received');
      handleExport();
    });
    
    const addMediaUnsub = eventBus.on('editor:addMedia', () => {
      logger.debug('BandSheetEditor', 'Add media event received');
      setMediaUrlModalOpen(true);
    });
    
    const deleteUnsub = eventBus.on('editor:delete', () => {
      logger.debug('BandSheetEditor', 'Delete sheet event received');
      handleDeleteSheet();
    });
    
    // Clean up event listeners
    return () => {
      newSheetUnsub();
      saveUnsub();
      pdfUnsub();
      addMediaUnsub();
      deleteUnsub();
    };
  }, [handleSave, handleNewSheet]);
  
  // Handle print button click - opens perform view with settings
  const handlePrintClick = () => {
    // Use the current sheet ID or the initial sheet ID
    const sheetId = currentSheetId || initialSheetId;
    
    if (!sheetId || sheetId === 'new') {
      showNotification('Please save the sheet before printing', 'warning');
      return;
    }
    
    // Generate print URL with current settings and open in new window
    const printUrl = generatePrintUrl(sheetId);
    window.open(printUrl, '_blank');
  };

  // Export handler that uses the context function
  const handleExport = () => {
    logger.debug('BandSheetEditor', 'Export handler called');
    exportSheet();
  };
  
  // PDF event handler consolidated into main event listener above
  
  // Make the PDF function globally accessible for direct access
  window.handleSheetExport = handleExport;

  // Set proper viewport for print mode - must be outside conditional for React Hooks rules
  useEffect(() => {
    if (isPrintMode) {
      const originalViewport = document.querySelector('meta[name="viewport"]');
      const printViewport = document.createElement('meta');
      printViewport.name = 'viewport';
      printViewport.content = 'width=device-width, initial-scale=1.0, user-scalable=yes';
      
      if (originalViewport) {
        originalViewport.replaceWith(printViewport);
      } else {
        document.head.appendChild(printViewport);
      }
      
      // Add print-specific styles to body
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.fontFamily = 'Arial, sans-serif';
      
      return () => {
        // Restore original viewport on cleanup
        if (originalViewport) {
          printViewport.replaceWith(originalViewport);
        }
        // Reset body styles
        document.body.style.margin = '';
        document.body.style.padding = '';
        document.body.style.fontFamily = '';
      };
    }
  }, [isPrintMode]);

  // If we're in perform mode, render the print-friendly version
  if (isPrintMode) {
    
    // Print view options
    const options = {
      includeChordProgressions: includeChords,
      includeSectionColors: includeColors
    };
    
    // Generate the print content directly in the component
    return (
      <div className="print-view print-mode">
        <style>
          {`
            /* Print and mobile styles for main sheet and chord progressions */
            .print-view {
              /* Force full width layout for print */
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 20px !important;
            }
            
            /* Mobile-only styles (only for normal view, not print mode) */
            @media (max-width: 768px) {
              .print-view:not(.print-mode) {
                /* Set a fixed width for mobile screens */
                width: 100% !important;
                max-width: none !important;
                font-size: 12pt !important;
              }
              
              /* Main sheet styles for mobile only (not print mode) */
              .print-view:not(.print-mode) .part-row {
                display: block !important;
                padding: 8px 0;
                border-bottom: 1px solid #eee;
              }
              
              /* Keep Part and Bars on the same row */
              .print-view:not(.print-mode) .part-row > div:nth-child(1),
              .print-view:not(.print-mode) .part-row > div:nth-child(2) {
                display: inline-block !important;
                width: 50%;
                vertical-align: top;
              }
              
              /* Other fields take full width */
              .print-view:not(.print-mode) .part-row > div:nth-child(3),
              .print-view:not(.print-mode) .part-row > div:nth-child(4),
              .print-view:not(.print-mode) .part-row > div:nth-child(5) {
                display: block !important;
                width: 100%;
                padding-top: 5px;
                clear: both;
              }
              
              /* Show field labels except for lyrics */
              .print-view:not(.print-mode) .part-row > div:not([data-label="Lyrics:"]):before {
                content: attr(data-label);
                font-weight: bold;
                display: inline-block;
                width: 60px;
                margin-right: 5px;
              }
              
              /* Chord progressions table mobile styles */
              .print-view:not(.print-mode) .chord-progressions-table {
                display: block !important;
              }
              
              /* Hide header in mobile */
              .print-view:not(.print-mode) .chord-progressions-table .row.bg-gray-100 {
                display: none !important;
              }
              
              /* Make rows stack vertically */
              .print-view:not(.print-mode) .chord-progressions-table .row {
                display: block !important;
                border-bottom: 1px solid #ddd;
                padding: 8px 0;
              }
              
              /* Keep Part and Bars on same row */
              .print-view:not(.print-mode) .chord-progressions-table .row > div:nth-child(1),
              .print-view:not(.print-mode) .chord-progressions-table .row > div:nth-child(2) {
                display: inline-block !important;
                width: 50%;
                border-right: none;
              }
              
              /* Transposed chords take full width */
              .print-view:not(.print-mode) .chord-progressions-table .row > div:nth-child(3) {
                display: block !important;
                width: 100%;
                border-right: none;
                margin-top: 5px;
                font-size: 0.85em;
                line-height: 1.3;
              }
              
              /* Show field labels except for chords */
              .print-view:not(.print-mode) .chord-progressions-table .row > div:not([data-label="Chords:"]):before {
                content: attr(data-label);
                font-weight: bold;
                display: inline-block;
                width: 60px;
              }
            }
            
            /* Force desktop layout when in perform mode (URL with ?perform=true) */
            .print-view .part-row {
              display: grid !important;
              grid-template-columns: 48px 48px 1fr 12.5% auto !important;
              gap: 10px !important;
              padding: 5px 16px !important;
              border-bottom: 1px solid #eee !important;
              align-items: center !important;
              min-height: auto !important;
            }
            
            /* Mobile-specific styles for print view URLs */
            @media (max-width: 767px) {
              .print-view .part-row {
                grid-template-columns: 40px 40px 1fr 60px auto !important;
                gap: 6px !important;
                padding: 5px 8px !important;
                font-size: 12px !important;
              }
              
              .print-view .lyrics {
                white-space: pre !important;
                overflow-x: auto !important;
                font-size: 11px !important;
              }
              
              .print-view .notes {
                font-size: 10px !important;
              }
              
              .print-view {
                font-size: 12px !important;
                padding: 5px !important;
              }
              
              .print-view .meta {
                font-size: 12px !important;
                gap: 8px !important;
              }
              
              .print-view .meta h1 {
                font-size: 16px !important;
              }
              
              .print-view .sheet-container {
                overflow-x: auto !important;
              }
            }
            
            /* Reset any mobile field styling for print view */
            .print-view .part-row > div:before {
              content: none !important;
            }
            
            .print-view .part-row > div {
              display: block !important;
              width: auto !important;
              padding-top: 0 !important;
              clear: none !important;
            }
            
            /* Force chord progressions to use desktop table layout in print view */
            .print-view .chord-progressions-table {
              display: grid !important;
              grid-template-columns: min-content min-content 1fr !important;
            }
            
            .print-view .chord-progressions-table .row.bg-gray-100 {
              display: contents !important;
            }
            
            .print-view .chord-progressions-table .row {
              display: contents !important;
              border-bottom: none !important;
              padding: 0 !important;
            }
            
            .print-view .chord-progressions-table .row > div {
              display: block !important;
              width: auto !important;
              border-right: 1px solid #ddd !important;
              margin-top: 0 !important;
              padding: 8px !important;
            }
            
            .print-view .chord-progressions-table .row > div:before {
              content: none !important;
            }
            
            /* Dedicated print styles for proper paper scaling */
            @media print {
              /* Set page size and margins */
              @page {
                size: A4;
                margin: 15mm;
              }
              
              /* Reset all viewport-dependent sizing for print */
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              
              .print-view {
                width: 100% !important;
                max-width: none !important;
                margin: 0 !important;
                padding: 0 !important;
                font-size: 11pt !important;
                line-height: 1.2 !important;
              }
              
              .print-button {
                display: none !important;
              }
              
              .page-break {
                page-break-before: always;
              }
              
              /* Ensure text and elements scale properly */
              h1, h2, h3, h4, h5, h6 {
                font-size: 14pt !important;
                page-break-after: avoid;
              }
              
              /* FORCE DESKTOP TABLE LAYOUT FOR PRINT - Override mobile styles */
              .print-view .part-row {
                display: grid !important;
                grid-template-columns: 48px 48px 1fr 12.5% auto !important;
                gap: 10px !important;
                padding: 5px 16px !important;
                border-bottom: 1px solid #eee !important;
                align-items: center !important;
                min-height: auto !important;
              }
              
              /* Reset mobile field label styles for print */
              .print-view .part-row > div:before {
                content: none !important;
              }
              
              /* Reset mobile field widths for print */
              .print-view .part-row > div {
                display: block !important;
                width: auto !important;
                padding-top: 0 !important;
                clear: none !important;
              }
              
              /* Force chord progressions table to use desktop layout */
              .chord-progressions-table {
                display: grid !important;
                grid-template-columns: min-content min-content 1fr !important;
              }
              
              /* Show header in print */
              .chord-progressions-table .row.bg-gray-100 {
                display: contents !important;
              }
              
              /* Make chord progression rows use grid layout */
              .chord-progressions-table .row {
                display: contents !important;
                border-bottom: none !important;
                padding: 0 !important;
              }
              
              /* Reset chord progression field layouts */
              .chord-progressions-table .row > div {
                display: block !important;
                width: auto !important;
                border-right: 1px solid #ddd !important;
                margin-top: 0 !important;
                padding: 8px !important;
              }
              
              /* Remove field labels from chord progressions in print */
              .chord-progressions-table .row > div:before {
                content: none !important;
              }
              
              /* Prevent content from being cut off */
              .section-container, .chord-progressions-table {
                page-break-inside: avoid;
              }
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.4;
              color: #333;
              max-width: 900px;
              margin: 0 auto;
              padding: 10px;
            }
            h1 {
              font-size: 20px;
              margin: 0 20px 0 0;
              display: inline-block;
            }
            .meta {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
              gap: 15px;
              margin-bottom: 10px;
              font-size: 14px;
              color: #555;
            }
            .sheet-container {
              border: 1px solid #ddd;
              border-radius: 4px;
              overflow: hidden;
              margin-top: 5px;
            }
            .sheet-header {
              display: none;
            }
            @media (min-width: 768px) {
              .sheet-header {
                display: grid;
                grid-template-columns: 80px 48px 48px 1fr 12.5% auto;
                gap: 10px;
                padding: 5px 16px;
                background-color: #f8f8f8;
                border-bottom: 1px solid #ddd;
                font-weight: bold;
                font-size: 14px;
              }
            }
            .section-container {
              display: flex;
              flex-direction: row;
              border-bottom: 1px solid #ddd;
              position: relative;
            }
            .section-container:last-child {
              border-bottom: none;
            }
            .section-header {
              width: 80px;
              min-width: 80px;
              padding: 8px 8px;
              border-right: 1px solid #ddd;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              position: relative;
            }
            .section-name {
              font-weight: bold;
              font-size: 13px;
            }
            .energy-line {
              position: absolute;
              bottom: 0;
              left: 0;
              height: 2px;
              background-color: black;
            }
            .parts-container {
              flex: 1;
            }
            .part-row {
              display: grid;
              grid-template-columns: 48px 48px 1fr 12.5% auto;
              gap: 10px;
              padding: 5px 16px;
              border-bottom: 1px solid #eee;
              align-items: center;
            }
            .part-row > div {
              width: auto;
              margin-bottom: 0;
            }
            .part-row > div:before {
              content: none;
            }
            .part-row:last-child {
              border-bottom: none;
            }
            .lyrics {
              white-space: pre-line;
              line-height: 1.3;
              font-family: 'Inconsolata', monospace;
            }
            .notes {
              font-size: 11px;
              color: #666;
              line-height: 1.2;
            }
            .print-button {
              padding: 5px 10px;
              background: #0066cc;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              margin-left: 10px;
            }
          `}
        </style>
        
        <div className="meta">
          <h1>{songData.title || 'Untitled'}</h1>
          {songData.artist && <div><strong>Artist:</strong> {songData.artist}</div>}
          {songData.bpm && <div><strong>BPM:</strong> {songData.bpm}</div>}
          {transposeValue !== 0 && (
            <div>
              <strong>Transposed:</strong> {transposeValue > 0 ? '+' + transposeValue : transposeValue} semitones
            </div>
          )}
          <button 
            className="print-button" 
            onClick={() => window.print()}
          >
            Print PDF
          </button>
        </div>
        
        <div className="sheet-container pb-20">
          {/* Sheet header */}
          <div className="sheet-header">
            <div>Section</div>
            <div>Part</div>
            <div>Bars</div>
            <div>Lyrics</div>
            <div>Notes</div>
            <div>{/* Actions placeholder */}</div>
          </div>
          
          {/* Sections */}
          {sections.map((section, sectionIndex) => (
            <div 
              key={section._id} 
              className="section-container"
              style={includeColors && section.backgroundColor ? { backgroundColor: section.backgroundColor } : {}}
            >
              {/* Energy indicator line */}
              <div 
                className="energy-line" 
                style={{ 
                  width: section.energy === 1 ? '80px' : 
                         section.energy === 10 ? '75%' : 
                         `${8 + ((75 - 8) / 9) * (section.energy - 1)}%`
                }}
              />
              
              {/* Section header */}
              <div className="section-header">
                <div className="section-name">{section.name}</div>
              </div>
              
              {/* Parts container */}
              <div className="parts-container">
                {section.parts.map((part, partIndex) => (
                  <div key={part._id} className="part-row">
                    <div data-label="Part:">{part.part}</div>
                    <div data-label="Bars:">{part.bars}</div>
                    <div data-label="Lyrics:" className="lyrics">{part.lyrics || ''}</div>
                    <div data-label="Notes:" className="notes">{part.notes || ''}</div>
                    <div>{/* No actions in print view */}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        {/* Chord progressions on page 2 */}
        {includeChords && partsModule && partsModule.length > 0 && (
          <div className="page-break">
            <h2 className="text-xl font-bold mt-8 mb-4">Chord Progressions</h2>
            <div className="border border-gray-300 rounded overflow-hidden chord-progressions-table" style={{ display: 'grid', gridTemplateColumns: 'min-content min-content 1fr' }}>
              <div className="bg-gray-100 font-bold row" style={{ display: 'contents' }}>
                <div className="p-2 border-r border-gray-300 whitespace-nowrap bg-gray-100">Part</div>
                <div className="p-2 border-r border-gray-300 text-center whitespace-nowrap bg-gray-100">Bars</div>
                <div className="p-2 bg-gray-100">Transposed Chords</div>
              </div>
              
              {partsModule.map(part => (
                <div key={part._id} className="row border-t border-gray-300" style={{ display: 'contents' }}>
                  <div className="p-2 border-r border-gray-300 font-semibold whitespace-nowrap" data-label="Part:">
                    {part.part}
                  </div>
                  <div className="p-2 border-r border-gray-300 text-center whitespace-nowrap" data-label="Bars:">
                    {part.bars || 4}
                  </div>
                  <div className="p-2 font-mono whitespace-pre-wrap" data-label="Chords:">
                    {part.chords 
                      ? (transposeValue === 0 ? part.chords : getTransposedChordsForPart(part.chords))
                      : ''}
                    {/* Add a debug comment of the current transpose value to help troubleshoot */}
                    {/* Transpose value: {transposeValue} */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Notation Section in Print Mode - check URL params for notation */}
        {searchParams.get('notation') !== 'false' && songData.notations && songData.notations.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3">Notation</h3>
            <div className="space-y-4">
              {songData.notations.map((notation, index) => {
                const notationContent = typeof notation === 'string' ? notation : notation?.content;
                const sectionRef = typeof notation === 'object' ? notation?.sectionRef : '';
                const title = typeof notation === 'object' ? notation?.title : '';
                
                return notationContent && (
                  <div key={index}>
                    <NotationSection 
                      notation={notationContent}
                      sectionRef={sectionRef}
                      title={title}
                      minimalMode={true}  // Hide all UI in perform mode
                      isEditing={false}
                      isPrintMode={true}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Comments Section in Print Mode - check URL params for comments */}
        {currentSheetId && searchParams.get('comments') === 'true' && (
          <div className="mt-6">
            <CommentsSection 
              objectType="sheet"
              objectId={currentSheetId}
              collapsed={false}
              printMode={true}
            />
          </div>
        )}
      </div>
    );
  }
  
  // Regular editor view
  return (
    <div className="flex h-full min-h-screen bg-background relative">
      {/* Left toolbar has been completely removed */}
      
      {/* Main content area - no more left margin needed */}
      <div className={`flex-1 w-full flex flex-col ${isMobile ? 'mt-10' : 'ml-0'} overflow-y-auto`}>
        {/* Container with max-width constraint for large screens */}
        <div className="w-full px-2 max-w-6xl mx-auto">
          {/* Local action buttons - moved inside content area */}
          {!isPrintMode && (
            <div className="mt-4 mb-2">
              <div className="flex gap-2 items-center">
                <button
                  onClick={handleNewSheet}
                  className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-2 transition-colors"
                  title="New Sheet"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">New</span>
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-2 transition-colors"
                  title="Save Sheet"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V2" />
                  </svg>
                  <span className="hidden sm:inline">Save</span>
                </button>
                {(() => {
                  const sheetId = currentSheetId || songData?._id || songData?.id || initialSheetId;
                  console.log('Share button check:', {
                    isAuthenticated: isAuthenticated(),
                    currentSheetId,
                    songData,
                    initialSheetId,
                    sheetId,
                    shouldShow: isAuthenticated() && sheetId
                  });
                  return isAuthenticated() && sheetId && (
                  <button
                    onClick={() => setShareModalOpen(true)}
                    className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-2 transition-colors"
                    title="Share Sheet"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 4.026a9.001 9.001 0 01-7.432 0m9.032-4.026A9.001 9.001 0 0112 3c-4.474 0-8.268 3.12-9.032 7.326m0 0A9.001 9.001 0 0112 21c4.474 0 8.268-3.12 9.032-7.326M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="hidden sm:inline">Share</span>
                  </button>
                  );
                })()}
                <button
                  onClick={handlePrintClick}
                  className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-2 transition-colors"
                  title="Preview Mode"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="hidden sm:inline">Preview</span>
                </button>
                <button
                  onClick={handleExport}
                  className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-2 transition-colors"
                  title="Export Sheet"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="hidden sm:inline">Export</span>
                </button>
                <button
                  onClick={() => setMediaUrlModalOpen(true)}
                  className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-2 transition-colors"
                  title="Add YouTube Link"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="hidden sm:inline">YouTube</span>
                </button>
                <button
                  onClick={handleDeleteSheet}
                  className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center gap-2 transition-colors"
                  title="Delete Sheet"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </div>
            </div>
          )}
          
          {/* Song info bar - positioned above the sheet in the visual stack */}
          <div className="mt-4 w-full">
            <SongInfoBar songData={songData} setSongData={setSongData} />
          </div>

          {/* Notation Section - MusicXML display */}

          {/* Sheet container - positioned below the SongInfoBar in the visual stack */}
          <div className="mt-4 mb-4 pb-10 bg-white rounded-md shadow border border-gray-200 w-full max-w-none">
            {/* Sheet header row */}
            <SheetHeader />

            {/* Sections */}
            {sections.map((section, si) => (
              <Section
                key={section._id}
                section={section}
                sectionIndex={si}
                hoverState={hoverState}
                setHoverState={setHoverState}
                isPrintMode={isPrintMode}
                showSection={showSection}
                showPart={showPart}
                showBars={showBars}
                showNotes={showNotes}
                handleContextMenu={handleContextMenu}
                placeholders={placeholders}
                // No longer passing editing-related props - they'll come from EditingContext
              />
            ))}
            
            {/* Add new section button at the bottom */}
            <div className="flex flex-col items-center justify-center mt-6 mb-4 cursor-pointer select-none group" onClick={() => addSection()}>
              <div className="text-2xl font-bold text-blue-600 group-hover:text-blue-800 leading-none">+</div>
              <div className="text-xs text-gray-500 group-hover:text-blue-700">Add Section</div>
            </div>
          </div>
          
          {/* Parts Module for chord progressions */}
          <div className="mt-4 mb-4 w-full">
            <PartsModule />
          </div>
          
          {/* Notation Section - Support for multiple music notations */}
          {!isPrintMode && (
            <div className="mt-4 mb-4 w-full">
              <div className="bg-white rounded-md shadow border border-gray-200 p-4">
                <h3 className="text-lg font-semibold mb-3">Notation</h3>
                
                {/* Display existing notations */}
                {songData.notations && songData.notations.length > 0 ? (
                  <div className="space-y-4">
                    {songData.notations.map((notation, index) => (
                      <div key={index} className="relative">
                        <NotationSection 
                          notation={typeof notation === 'string' ? notation : notation?.content}
                          sectionRef={typeof notation === 'object' ? notation?.sectionRef : ''}
                          title={typeof notation === 'object' ? notation?.title : ''}
                          onNotationChange={(updatedNotation) => {
                            const newNotations = [...(songData.notations || [])];
                            if (updatedNotation) {
                              // Keep as object structure
                              if (typeof newNotations[index] === 'string') {
                                newNotations[index] = { content: updatedNotation };
                              } else {
                                newNotations[index] = { ...newNotations[index], content: updatedNotation };
                              }
                            } else {
                              // Remove notation if cleared
                              newNotations.splice(index, 1);
                            }
                            setSongData(prev => ({ ...prev, notations: newNotations }));
                          }}
                          onSectionRefChange={(sectionRef) => {
                            const newNotations = [...(songData.notations || [])];
                            if (typeof newNotations[index] === 'string') {
                              newNotations[index] = { content: newNotations[index], sectionRef };
                            } else {
                              newNotations[index] = { ...newNotations[index], sectionRef };
                            }
                            setSongData(prev => ({ ...prev, notations: newNotations }));
                          }}
                          onTitleChange={(title) => {
                            const newNotations = [...(songData.notations || [])];
                            if (typeof newNotations[index] === 'string') {
                              newNotations[index] = { content: newNotations[index], title };
                            } else {
                              newNotations[index] = { ...newNotations[index], title };
                            }
                            setSongData(prev => ({ ...prev, notations: newNotations }));
                          }}
                          isEditing={true}
                          isPrintMode={false}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">No notation added yet</div>
                )}
                
                {/* Add new notation button */}
                <button
                  onClick={() => {
                    const newNotations = [...(songData.notations || []), { content: '', sectionRef: '', title: '' }];
                    setSongData(prev => ({ ...prev, notations: newNotations }));
                  }}
                  className="mt-3 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors text-sm"
                >
                  Add Notation
                </button>
              </div>
            </div>
          )}
          
          {/* Sheet Lineup Manager */}
          <div className="mt-4 mb-4 w-full">
            <SheetLineupManager sheetId={currentSheetId} />
          </div>

          {/* Comments Section - only show if we have a saved sheet */}
          {currentSheetId && !isPrintMode && (
            <div className="mt-4 mb-4 w-full">
              <CommentsSection 
                objectType="sheet"
                objectId={currentSheetId}
                collapsed={false}
                printMode={false}
              />
            </div>
          )}
        </div>
      </div>

      {/* Floating UI elements */}
      <ContextMenu menuItems={getContextMenuItems()} />
      
      {/* Color picker */}
      {colorPicker.isOpen && (
        <ColorPicker
          initialColor={colorPicker.initialColor}
          x={colorPicker.x}
          y={colorPicker.y}
          onChange={(color) => {
            // Update the section background color
            updateSectionBackgroundColor(colorPicker.sectionIndex, color);
            
            // Log the update for debugging
            logger.debug('BandSheetEditor', `Color changed for section: ${colorPicker.sectionIndex} to: ${color}`);
            
            // Force an immediate save to ensure the color is persisted
            setTimeout(() => {
              logger.debug('BandSheetEditor', 'Auto-saving after color change');
              saveCurrentSheet();
            }, 500);
          }}
          onClose={() => {
            logger.debug('BandSheetEditor', 'Color picker closed, saving final color');
            setColorPicker(prev => ({ ...prev, isOpen: false }));
            
            // Also save when closing the color picker
            setTimeout(() => {
              logger.debug('BandSheetEditor', 'Final save after color picker closed');
              saveCurrentSheet();
            }, 500);
          }}
        />
      )}
      
      {/* Energy Dialog */}
      <EnergyDialog />
      
      {/* New Sheet Confirmation Dialog */}
      <ConfirmModal
        isOpen={newSheetConfirm.isOpen}
        title="Save Current Sheet"
        message="Save your current sheet before creating a new one?"
        confirmText="Save"
        cancelText="Cancel"
        confirmColor="green"
        onConfirm={() => {
          newSheetConfirm.onConfirm();
          setNewSheetConfirm(prev => ({ ...prev, isOpen: false }));
        }}
        onCancel={() => {
          newSheetConfirm.onCancel();
          setNewSheetConfirm(prev => ({ ...prev, isOpen: false }));
        }}
      />
      
      {/* Delete Sheet Confirmation Dialog */}
      <ConfirmModal
        isOpen={deleteSheetConfirm.isOpen}
        title="Delete Sheet"
        message={`Are you sure you want to delete "${songData.title || 'this sheet'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="red"
        onConfirm={() => {
          deleteSheetConfirm.onConfirm();
          setDeleteSheetConfirm(prev => ({ ...prev, isOpen: false }));
        }}
        onCancel={() => {
          deleteSheetConfirm.onCancel();
          setDeleteSheetConfirm(prev => ({ ...prev, isOpen: false }));
        }}
      />
      
      {/* Media URL Modal */}
      <MediaUrlModal
        isOpen={mediaUrlModalOpen}
        onClose={() => setMediaUrlModalOpen(false)}
        onSave={handleMediaUrlSave}
        currentUrl={songData.mediaUrl || ''}
      />
      
      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        sheet={{ ...songData, _id: currentSheetId || songData?._id || songData?.id || initialSheetId, id: currentSheetId || songData?._id || songData?.id || initialSheetId }}
        onShareUpdate={(updatedSheet) => {
          setSongData(updatedSheet);
          setShareModalOpen(false);
        }}
      />
      
      {/* Notifications are now rendered via the centralized Notification component */}
      
      {/* Mobile sidebar button removed */}
    </div>
  );
}
